import type { FeatureFlag } from '@risksmart-app/modules/src/index';

import { useIsCustomerSupport } from '@/hooks/useIsCustomerSupport';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';

export const FORM_PREFILL_FLAG = 'ai_agent_form_prefill' satisfies FeatureFlag;

/**
 * The single gate for suggest-and-confirm form prefill (AID-382; spec
 * `docs/ai-chat-form-prefill-spec.md` §8).
 *
 * Three conditions, all required:
 *
 * 1. **The per-org flag.** Off by default everywhere.
 * 2. **The `ai.subModules.agent` entitlement.** Prefill is an Agent-mode
 *    capability — the proposal arrives over the agent socket and renders in the
 *    agent chat. With the module off there is no chat to propose into, so
 *    publishing a form descriptor would be pure egress for no feature.
 * 3. **Internal users only**, for cut 1. Mirrors the `isDraftPrompt` precedent
 *    in `AIChatSidePanel` (`useIsCustomerSupport`), which is how this codebase
 *    already ships an agent capability to staff before customers.
 *
 * Deliberately ONE hook rather than a check per call site: the publisher (which
 * decides whether `pageContext.activeForm` is sent at all) and the ProposalCard
 * (which decides whether the user can apply anything) have to agree. If they
 * drifted, the failure is silent and lopsided — a descriptor egressing for a
 * feature the user cannot see, or a card offering Apply against a form that
 * never published.
 *
 * Note this gates the CLIENT half only. The agent will not register
 * `propose_form_prefill` at all unless `FORM_PREFILL_ENABLED` is set on the
 * ai-agent runtime (AID-380), so flag-on in an environment where the agent side
 * is off degrades to "no proposal ever arrives" rather than to a broken card.
 */
export const useIsFormPrefillEnabled = (): boolean => {
  const flagEnabled = useIsFeatureFlagEnabled(FORM_PREFILL_FLAG);
  const agentEnabled = useIsModuleEnabled('ai.subModules.agent');
  const isInternalUser = useIsCustomerSupport();

  return flagEnabled && agentEnabled && isInternalUser;
};
